import { useState, useMemo } from 'react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { useCategories } from './hooks/useCategories';
import { permissionEngine } from '@/core/permissions';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { Plus, Edit2, Trash2, FolderTree, Save, ChevronDown, ChevronRight, ChevronLeft } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Card } from '@/components/ui/Card';
import { Input, Select } from '@/components/ui';
import { useForm } from 'react-hook-form';
import type { InventoryCategory, CategoryCreatePayload } from './types/categories.types';

export function CategoriesPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  permissionEngine.assert(profile?.id || '', 'agro.view');

  const { data, isLoading, createCategory, updateCategory, deleteCategory, isCreating, isUpdating, isDeleting } = useCategories();
  
  const [selectedItem, setSelectedItem] = useState<InventoryCategory | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InventoryCategory | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleCollapse = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isVisible = (item: any) => {
    if (!item.ancestorIds || item.ancestorIds.length === 0) return true;
    return item.ancestorIds.every((id: string) => expandedNodes.has(id));
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryCreatePayload>({
    defaultValues: {
      company_id: companyId,
      name: '',
      code: '',
      description: '',
      parent_id: '',
      is_active: true,
      sort_order: 0,
    },
  });

  const sortedData = useMemo(() => {
    const childrenMap = new Map<string, InventoryCategory[]>();
    const roots: InventoryCategory[] = [];

    data.forEach(item => {
      if (!item.parent_id) {
        roots.push(item);
      } else {
        if (!childrenMap.has(item.parent_id)) {
          childrenMap.set(item.parent_id, []);
        }
        childrenMap.get(item.parent_id)!.push(item);
      }
    });

    const sortFn = (a: InventoryCategory, b: InventoryCategory) => {
      if (a.sort_order !== b.sort_order) {
        return (a.sort_order || 0) - (b.sort_order || 0);
      }
      return a.name.localeCompare(b.name);
    };

    const flattened: (InventoryCategory & { sequence: string; hasChildren: boolean })[] = [];

    const traverse = (items: InventoryCategory[], prefix: string, level: number, ancestorIds: string[]) => {
      items.sort(sortFn).forEach((item, index) => {
        const seq = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
        const children = childrenMap.get(item.id);
        const hasChildren = !!children && children.length > 0;
        flattened.push({ ...item, sequence: seq, hasChildren, computedLevel: level, ancestorIds });
        if (hasChildren) {
          traverse(children, seq, level + 1, [...ancestorIds, item.id]);
        }
      });
    };

    traverse(roots, '', 1, []);
    return flattened;
  }, [data]);

  const handleSelect = (item: InventoryCategory) => {
    setSelectedItem(item);
    setIsEditing(false);
    setIsCreatingNew(false);
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setIsCreatingNew(true);
    setIsEditing(true);
    reset({
      company_id: companyId,
      name: '',
      code: '',
      description: '',
      parent_id: selectedItem?.id || '',
      is_active: true,
      sort_order: 0,
    });
  };

  const handleEdit = () => {
    if (!selectedItem) return;
    setIsEditing(true);
    setIsCreatingNew(false);
    reset({
      company_id: selectedItem.company_id,
      name: selectedItem.name,
      code: selectedItem.code || '',
      description: selectedItem.description || '',
      parent_id: selectedItem.parent_id || '',
      is_active: selectedItem.is_active,
      sort_order: selectedItem.sort_order || 0,
    });
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteCategory(deleteTarget.id);
      setDeleteTarget(null);
      if (selectedItem?.id === deleteTarget.id) {
        setSelectedItem(null);
        setIsEditing(false);
      }
    }
  };

  const onSubmit = async (formData: CategoryCreatePayload) => {
    const payload = {
      ...formData,
      parent_id: formData.parent_id || null,
    };

    if (isCreatingNew) {
      await createCategory(payload);
      setIsCreatingNew(false);
      setIsEditing(false);
    } else if (selectedItem) {
      await updateCategory({ id: selectedItem.id, payload });
      setIsEditing(false);
    }
  };

  const availableParents = sortedData.filter((c: any) => {
    if (isCreatingNew) return true;
    if (!selectedItem) return true;
    if (c.id === selectedItem.id) return false;
    if (c.path?.includes(selectedItem.id)) return false;
    return true;
  });

  const handleInitDefaults = async () => {
    const defaults = [
      { name: 'Thi công', code: 'TC', sort_order: 1, is_active: true, company_id: companyId, parent_id: null, description: '' },
      { name: 'Bảo dưỡng', code: 'BD', sort_order: 2, is_active: true, company_id: companyId, parent_id: null, description: '' },
      { name: 'Khác', code: 'KHAC', sort_order: 3, is_active: true, company_id: companyId, parent_id: null, description: '' }
    ];
    for (const item of defaults) {
      await createCategory(item);
    }
  };

  return (
    <PageLayout title="Nhóm vật tư" description="Quản lý danh mục, chủng loại và nhóm vật tư">
      <div className="grid h-full min-h-0 gap-4 lg:grid-cols-12 lg:items-start">
        {/* CỘT TRÁI - DANH SÁCH */}
        <Card className={`flex h-full min-h-[500px] flex-col border-slate-200 bg-white shadow-sm lg:col-span-5 xl:col-span-4 lg:sticky lg:top-0 lg:max-h-[calc(100vh-8rem)] ${selectedItem || isCreatingNew ? 'hidden lg:flex' : 'flex'}`}>
          <div className="flex shrink-0 items-center justify-between border-b border-slate-100 p-4">
            <h3 className="text-sm font-medium tracking-wide text-emerald-800">Cây danh mục</h3>
            <AuthButton
              module="agro"
              action="create"
              behavior="disable"
              onClick={handleAdd}
              className="flex h-8 items-center gap-1.5 rounded bg-emerald-600 px-3 text-xs font-medium text-white transition-colors hover:bg-emerald-700"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Thêm mới</span>
            </AuthButton>
          </div>
          
          <div className="flex-1 overflow-y-auto p-2">
            {isLoading ? (
              <div className="p-8 text-center text-xs text-slate-500">Đang tải dữ liệu...</div>
            ) : sortedData.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FolderTree className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                <p className="mb-4 text-xs">Chưa có danh mục nào</p>
                <AuthButton
                  module="agro"
                  action="create"
                  behavior="hide"
                  onClick={handleInitDefaults}
                  disabled={isCreating}
                  className="mx-auto flex h-8 items-center gap-1.5 rounded border border-emerald-600 px-3 text-xs font-medium text-emerald-600 transition-colors hover:bg-emerald-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Khởi tạo 3 mục mặc định</span>
                </AuthButton>
              </div>
            ) : (
              <div className="space-y-0.5">
                {sortedData.map((item: any) => {
                  if (!isVisible(item)) return null;

                  return (
                    <div
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      className={`group flex cursor-pointer items-center justify-between rounded px-2 py-2 transition-colors ${selectedItem?.id === item.id && !isCreatingNew ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                    >
                      <div className="flex items-center gap-2" style={{ paddingLeft: `${(item.computedLevel > 1 ? item.computedLevel - 1 : 0) * 1.5}rem` }}>
                        {item.hasChildren ? (
                          <button
                            onClick={(e) => toggleCollapse(item.id, e)}
                            className="flex h-5 w-5 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
                          >
                            {expandedNodes.has(item.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        ) : (
                          <div className="w-5" />
                        )}
                        <span className={`text-sm ${selectedItem?.id === item.id && !isCreatingNew ? 'font-medium text-emerald-700' : 'text-slate-700'}`}>
                          <span className="mr-1 text-slate-400">{item.sequence}.</span>
                          {item.name}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {/* CỘT PHẢI - CHI TIẾT */}
        <Card className={`flex min-h-[500px] flex-col border-slate-200 bg-white shadow-sm lg:col-span-7 xl:col-span-8 ${!selectedItem && !isCreatingNew ? 'hidden lg:flex' : 'flex'}`}>
          {!selectedItem && !isCreatingNew ? (
            <div className="flex flex-1 flex-col items-center justify-center p-12 text-slate-400">
              <FolderTree className="mb-4 h-16 w-16 text-slate-200" />
              <p className="text-sm font-medium text-slate-600">Chọn một danh mục để xem chi tiết</p>
              <p className="mt-1 text-xs">Hoặc nhấn "Thêm mới" để tạo nhóm vật tư mới</p>
            </div>
          ) : (
            <>
              <div className="flex shrink-0 items-center justify-between border-b border-slate-100 p-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedItem(null);
                      setIsCreatingNew(false);
                      setIsEditing(false);
                    }}
                    className="flex lg:hidden h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 -ml-2"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <h3 className="text-sm font-medium tracking-wide text-emerald-800">
                    {isCreatingNew ? 'Thêm mới danh mục' : 'Chi tiết danh mục'}
                  </h3>
                </div>
                {!isEditing && selectedItem && (
                  <div className="flex items-center gap-2">
                    <AuthButton
                      module="agro"
                      action="delete"
                      behavior="hide"
                      onClick={() => setDeleteTarget(selectedItem)}
                      className="flex h-8 items-center gap-1.5 rounded bg-rose-600 px-3 text-xs font-medium text-white transition-colors hover:bg-rose-700"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Xóa</span>
                    </AuthButton>
                    <AuthButton
                      module="agro"
                      action="update"
                      behavior="disable"
                      onClick={handleEdit}
                      className="flex h-8 items-center gap-1.5 rounded bg-emerald-600 px-3 text-xs font-medium text-white transition-colors hover:bg-emerald-700"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                      <span>Chỉnh sửa</span>
                    </AuthButton>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {!isEditing && selectedItem ? (
                  // VIEW MODE
                  <div className="space-y-6">
                    <div className="grid gap-6 sm:grid-cols-2">
                      <div>
                        <p className="text-xs text-slate-500">Tên danh mục</p>
                        <p className="mt-1 text-sm font-medium text-slate-900">{selectedItem.name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Mã danh mục</p>
                        <p className="mt-1 text-sm font-medium text-slate-900">{selectedItem.code || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Thuộc nhóm (Danh mục cha)</p>
                        <p className="mt-1 text-sm font-medium text-slate-900">
                          {selectedItem.parent_id ? data.find(c => c.id === selectedItem.parent_id)?.name || '-' : '-- Cấp cao nhất --'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Trạng thái</p>
                        <div className="mt-1">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${selectedItem.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                            {selectedItem.is_active ? 'Đang hoạt động' : 'Đã ẩn'}
                          </span>
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-xs text-slate-500">Mô tả chi tiết</p>
                        <p className="mt-1 text-sm text-slate-900">{selectedItem.description || '-'}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // EDIT MODE
                  <form id="category-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs font-medium text-slate-700">Tên danh mục <span className="text-rose-500">*</span></label>
                        <Input {...register('name', { required: 'Vui lòng nhập tên danh mục' })} placeholder="VD: Cây bóng mát..." className="w-full text-sm" />
                        {errors.name && <p className="mt-1 text-xs text-rose-500">{errors.name.message}</p>}
                      </div>
                      
                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-700">Mã danh mục <span className="text-rose-500">*</span></label>
                        <Input {...register('code', { required: 'Vui lòng nhập mã' })} placeholder="VD: CBM" className="w-full text-sm uppercase" />
                        {errors.code && <p className="mt-1 text-xs text-rose-500">{errors.code.message}</p>}
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-700">Thứ tự sắp xếp</label>
                        <Input type="number" {...register('sort_order', { valueAsNumber: true })} className="w-full text-sm" />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs font-medium text-slate-700">Thuộc nhóm (Danh mục cha)</label>
                        <Select {...register('parent_id')} className="w-full text-sm">
                          <option value="">-- Là danh mục cấp cao nhất --</option>
                          {availableParents.map(p => (
                            <option key={p.id} value={p.id}>
                              {'--'.repeat(p.level - 1)} {p.sequence}. {p.name}
                            </option>
                          ))}
                        </Select>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs font-medium text-slate-700">Mô tả</label>
                        <textarea {...register('description')} rows={3} placeholder="Mô tả nhóm vật tư..." className="field w-full text-sm py-2" />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" {...register('is_active')} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                          <span className="text-sm text-slate-700">Đang hoạt động (Hiển thị)</span>
                        </label>
                      </div>
                    </div>
                  </form>
                )}
              </div>
              
              {isEditing && (
                <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 p-4">
                  <button
                    type="button"
                    onClick={() => {
                      if (isCreatingNew) {
                        setIsCreatingNew(false);
                        setSelectedItem(null);
                      } else {
                        setIsEditing(false);
                      }
                    }}
                    className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-200"
                  >
                    Hủy bỏ
                  </button>
                  <AuthButton
                    module="agro"
                    action={isCreatingNew ? 'create' : 'update'}
                    behavior="disable"
                    type="submit"
                    form="category-form"
                    isLoading={isCreating || isUpdating}
                    className="flex items-center gap-2 rounded-md bg-emerald-600 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700"
                  >
                    <Save className="h-4 w-4" />
                    <span>Lưu lại</span>
                  </AuthButton>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Xác nhận xóa danh mục"
        message={
          <span>
            Bạn có chắc chắn muốn xóa danh mục <strong>{deleteTarget?.name}</strong>?
            Toàn bộ vật tư thuộc danh mục này sẽ mất phân loại.
          </span>
        }
        confirmText="Xóa danh mục"
        cancelText="Hủy"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </PageLayout>
  );
}

export default CategoriesPage;
